import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import merge from "lodash/merge";
import { DEFAULT_AXNUI_CONTENT, DEFAULT_AXNUI_THEME, DEFAULT_AXNUI_LAYOUT } from "@iddataweb/idw-common-ui/constants";
import { setError } from "@redux/actions/error-actions";
import {
    setVerificationConfiguration,
    setVerificationConfigurationLoading,
    setVerificationConfigurationLoadingFailed
} from "@redux/actions/verification-configuration-actions";
import { setVerificationForm } from "@redux/actions/verification-form-actions";
import { getVerificationConfiguration } from "@requests/get-verification-configuration";
import { Logger } from "@utils/logger";
import { datadogRum } from '@datadog/browser-rum';
import { useLocation, useNavigate } from "react-router-dom";
import { getQueryParametersState } from "@redux/selectors/query-parameters-selectors";
import { navigateToUtilWithQueryParameters, handleNavigationForError } from "@utils/navigation-util";
import { setLanguageContent } from "@redux/actions/language-actions";
import { ConfigurationResponse } from "@idw-types/data";
import { InternationalAddressCountryTypes, LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";

export const useInitializeApplication = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const queryParametersState = useSelector(getQueryParametersState);

    const handleInvalidQueryParameters = () => {
        Logger.warn(`[App] No ASI found in the url`, null, queryParametersState);
        Logger.error(`[App] Critical: Unknown state for the application.`);
        const dataDogError = new Error(`[App] Critical: Unknown state for the application.`);
        datadogRum.addError(dataDogError);
        dispatch(setLanguageContent(DEFAULT_AXNUI_CONTENT[queryParametersState.language || LocaleTypes.ENGLISH]));
        dispatch(setError({
            name: "ASI Error",
            message: `Pressing the browser's "back" or "refresh" button is prohibited. Please restart the process.`,
            asi: queryParametersState.asi,
            errorCode: null,
            httpStatus: null,
            errorURI: window.location.href
        }));
        dispatch(setVerificationConfigurationLoadingFailed(true));
        navigateToUtilWithQueryParameters("/error", navigate, location.pathname, location.search);

    };

    const loadFakeVerificationConfiguration = () => {
        const content: AXNUIContent = DEFAULT_AXNUI_CONTENT[queryParametersState.language || LocaleTypes.ENGLISH];

        dispatch(setVerificationConfiguration({
            apikey: "not-provided",
            asi: "not-provided",
            AttributeInputList: [],
            countryCode: InternationalAddressCountryTypes.US,
            language: queryParametersState.language || LocaleTypes.ENGLISH,
            properties: {
                content: content,
                theme: DEFAULT_AXNUI_THEME,
                layout: DEFAULT_AXNUI_LAYOUT,
                useThemedUI: true,
            },
            rpName: "Document Capture Complete",
            serviceName: "Document Capture Complete",
            status: "success",
        }));
        dispatch(setVerificationForm([]));
    };

    const loadVerificationConfiguration = async () => {
        let redirectToCallbackUrlOnError;
        let oidcCallbackURL;
        let clearConfigOnError = true;

        try {
            const response = await getVerificationConfiguration(queryParametersState);
            Logger.info(`[App] Response obtained from the config API call: ${response}`);

            const verifyPageData: ConfigurationResponse = await response.json();

            redirectToCallbackUrlOnError = verifyPageData.properties?.redirectToCallbackUrlOnError;
            oidcCallbackURL = verifyPageData.oidcCallbackURL;

            if (verifyPageData.status === "success") {
                Logger.info(`[App] verification configuration loaded`, verifyPageData);

                if (verifyPageData.properties?.theme) {
                    verifyPageData.properties.theme = merge({}, DEFAULT_AXNUI_THEME, verifyPageData.properties.theme);
                }
                if (verifyPageData.properties?.layout) {
                    verifyPageData.properties.layout = merge({}, DEFAULT_AXNUI_LAYOUT, verifyPageData.properties.layout);
                }

                dispatch(setVerificationConfiguration(verifyPageData));
                clearConfigOnError = false;
            }

            if (verifyPageData.AttributeInputList) {
                dispatch(setVerificationForm(verifyPageData.AttributeInputList));
            } else if (verifyPageData.errorURL) {
                window.location.href = verifyPageData.errorURL;
            } else {
                const error = {
                    asi: queryParametersState.asi,
                    errorCode: verifyPageData.errorCode || response.statusText,
                    message: verifyPageData.errorMessage || "Missing verification configuration data",
                    errorURI: response.url,
                    status: `${response.status}`,
                    forwardedErrorCode: verifyPageData.errorCode,
                    forwardedErrorDescription: verifyPageData.errorMessage
                }
                throw error;
            }
        } catch (error) {
            console.error(error);
            console.dir(error);
            Logger.error('[App] Fetch error', error);
            dispatch(setLanguageContent(DEFAULT_AXNUI_CONTENT[queryParametersState.language || LocaleTypes.ENGLISH]));
            dispatch(setError({
                name: "Verification Configuration Loading Error",
                message: error.message,
                asi: queryParametersState.asi,
                errorCode: error.errorCode,
                errorURI: error.errorURI,
                httpStatus: error.status,
            }));
            dispatch(setVerificationConfigurationLoadingFailed(clearConfigOnError));
            Logger.error(`[App] Configuration retrieval failed`, error);
            const dataDogError = new Error(`[App] Configuration retrieval failed`);
            datadogRum.addError(dataDogError, {
                error
            });
            handleNavigationForError(dispatch)(
                redirectToCallbackUrlOnError,
                oidcCallbackURL,
                queryParametersState.asi,
                "/error",
                navigate,
                location.pathname,
                location.search,
                error
            );
        }
    }

    const initializeApplication = () => {
        // normal flow - hit authenticated config route
        if (queryParametersState.configUrl &&
            ((queryParametersState.asi && queryParametersState.token) ||
                (queryParametersState.asi && queryParametersState.flow))
        ) {
            Logger.info(`[App] Retrieving config from url: ${queryParametersState.configUrl}`);
            dispatch(setVerificationConfigurationLoading(true));
            loadVerificationConfiguration();
        }
        // themed error flow - hit unauthenticated config route and navigate to error page
        else if (
            queryParametersState.configUrl &&
            queryParametersState.asi &&
            queryParametersState.errorCode
        ) {
            Logger.info(`[App] Navigating to error page - token not provided`);
            navigateToUtilWithQueryParameters("/error", navigate, location.pathname, location.search);
            Logger.info(`[App] Retrieving config from url: ${queryParametersState.configUrl}`);
            dispatch(setVerificationConfigurationLoading(true));
            loadVerificationConfiguration();
        }
        // special case - static loading of mobile_complete flow
        else if (
            queryParametersState.configUrl === null &&
            queryParametersState.flow &&
            queryParametersState.flow === "mobile_complete"
        ) {
            Logger.info(`[App] Navigating to mobile_complete page`);
            dispatch(setVerificationConfigurationLoading(true));
            loadFakeVerificationConfiguration();
        }
        // error flow - dont hit config route
        else if (queryParametersState.referenceId && queryParametersState.errorCode) {
            dispatch(setLanguageContent(DEFAULT_AXNUI_CONTENT[queryParametersState.language || LocaleTypes.ENGLISH]));
            dispatch(setVerificationConfigurationLoadingFailed(true));
            navigateToUtilWithQueryParameters("/error", navigate, location.pathname, location.search);
        } else {
            handleInvalidQueryParameters();
        }
    };

    useEffect(() => {
        if (queryParametersState.initialized) {
            initializeApplication();
        }
    }, [queryParametersState.initialized]);
};